import { WatchfaceDeviceType } from '@/hooks';
import { dataSourceWholeItems } from '@/resources/data-source';
import { canUse } from '@/utils/can-use.utils';
import { cloneDeep } from 'lodash-es';

/**
 * 1.5天气代码
 * 1.5工具仅支持到N62, 且N62有冻雨，其他的没有冻雨
 */
const editor15Weather = {
  // 需要使用1.5版本天气的设备
  deviceTypes: ['N62', 'N65', 'M66', 'M67'],
  // 需要将阵雨当做小雨处理的设备
  // showerAsLightRainDeviceTypes: ['M66', 'M67', 'N62'],
  // 1.5天气对照表
  values: {
    0: '晴(必选)',
    1: '多云(必选)',
    2: '阴(必选)',
    3: '阵雨',
    4: '雷阵雨(必选)',
    5: '冰雹(必选)',
    6: '雨夹雪',
    7: '小雨',
    8: '中雨',
    9: '大雨',
    10: '暴雨',
    13: '阵雪',
    14: '小雪',
    15: '中雪',
    16: '大雪',
    17: '暴雪',
    18: '雾(必选)',
    19: '冻雨',
    20: '沙尘暴(必选)',
    29: '浮尘',
    30: '扬沙',
    33: '雪(必选)',
    53: '霾(必选)',
    301: '雨(必选)',
    99: '未知(必选)'
  }
};

/**
 * 处理阵雨
 * 2.0将阵雨归为了中雨，但部分设备不支持，需要转换为小雨
 */
const exchangeShowerRainIfNeed = (
  weatherRange: DataSourceWeatherValueItem[],
  deviceType: WatchfaceDeviceType
) => {
  // 暂时处理这几个设备，其他设备不处理
  const deviceTypes = editor15Weather.deviceTypes ?? [];
  if (!deviceTypes.includes(deviceType)) return;

  const showerRaincode = 3;
  // 将阵雨从中雨中去除，
  const item1 = weatherRange.find((item) => item.label == '中雨');
  if (item1) {
    item1.values = item1.values?.filter((v) => v != showerRaincode);
  }
  // 将小雨添加到中雨中
  const item2 = weatherRange.find((item) => item.label == '小雨');
  if (item2) {
    item2.values = [...(item2.values ?? []), showerRaincode];
  }
};

export const getWeatherAstIndexs = (options: {
  type: 'whole' | 'simple';
  deviceType: WatchfaceDeviceType;
  fullWeatherCode?: boolean;
}) => {
  const res: {
    value: number;
    imageIndex: number;
    label?: string;
  }[] = [];
  const { type, deviceType, fullWeatherCode = false } = options;
  const sourceItem = dataSourceWholeItems.find(
    (item) => item.key == 'weatherCurrentWeather'
  );
  const weatherRange = cloneDeep(sourceItem?.weatherValueRange?.[type] ?? []);

  // 如果返回全部天气代码，一般只在编辑器中使用
  if (fullWeatherCode) {
    weatherRange?.forEach((item, index) => {
      [...new Set([item.value, ...(item.values ?? [])])].forEach((_value) => {
        res.push({
          value: _value,
          imageIndex: index,
          label: item.label
        });
      });
    });
  } else if (canUse('new-weather-comparison', deviceType).valid) {
    // 按照新天气代码对照表：即18或11个天气
    // 由于11个图片天气固件存在bug，需要转为18个图片格式
    const wholeRange = sourceItem?.weatherValueRange?.whole ?? [];
    const simpleRange = sourceItem?.weatherValueRange?.simple ?? [];
    if (type == 'whole') {
      wholeRange.forEach((item, index) => {
        res.push({
          value: item.value,
          imageIndex: index,
          label: item.label
        });
      });
    } else {
      // 11个版本的，需要模拟为18个版本
      wholeRange.forEach((item) => {
        // 寻找对应的11版本的index
        const index = simpleRange.findIndex((_item) => {
          const value = [...(item.values ?? []), item.value];
          const _value = [...(_item.values ?? []), _item.value];
          return value.some((v) => _value.includes(v));
        });
        res.push({
          value: item.value,
          imageIndex: index,
          label: simpleRange[index]?.label + '->' + item.label
        });
      });
    }
  } else {
    // 判断是否使用1.5版本天气
    const editort15Values = editor15Weather.deviceTypes.includes(deviceType)
      ? Object.keys(editor15Weather.values)
      : [];
    // 使用1.5版本的天气代码
    if (editort15Values.length) {
      exchangeShowerRainIfNeed(weatherRange, deviceType);
      editort15Values.forEach((_value) => {
        const value = Number(_value);
        // 获取需要使用的图片
        const rangeIndex = weatherRange.findIndex((item) =>
          [item.value, ...(item.values ?? [])].includes(value)
        );
        if (rangeIndex < 0) throw '1.5天气代码和2.0不兼容';
        res.push({
          value: value,
          imageIndex: rangeIndex,
          label: `1.5${
            editor15Weather['values'][String(_value) as '1']
          }->对应2.0${weatherRange[rangeIndex].label}`
        });
      });
    } else {
      // 未使用1.5且未使用新天气对照码
      weatherRange?.forEach((item, index) => {
        [...new Set([item.value, ...(item.values ?? [])])].forEach((_value) => {
          res.push({
            value: _value,
            imageIndex: index,
            label: '共用' + item.label
          });
        });
      });
    }
  }
  res.sort((a, b) => a.value - b.value);
  return res;
};
